/**
 * 차트 공통 축 서식.
 *
 * 그리드·축은 **실선 헤어라인 한 톤**이다. 파선을 쓰면 눈에 띄어 임계선으로
 * 오독된다 — 임계선은 별도로 `markLine` 이 그린다 (§8.6.2 금지 목록).
 */

/**
 * 카드 안 차트의 표준 높이.
 *
 * 위젯마다 220·240 처럼 다른 값을 쓰면 같은 행의 플롯이 다른 높이로 그려져
 * 눈금선이 어긋난다. 카드 높이는 CSS 가 맞춰 주지만 **플롯 높이는 여기서 맞춘다.**
 * 행 수에 비례해 늘어나는 차트(순위·아령)는 예외이며 그때도 최소값으로 이 값을 쓴다.
 */
import { cssVar } from './cssvar'

export const CHART_H = 240

const NICE = [1, 2, 2.5, 5, 10]

/**
 * 데이터 범위 → **눈금이 고른** 축 경계.
 *
 * min/max 를 데이터 값 그대로 박아 두면 ECharts 가 양 끝에만 어중간한 눈금을
 * 만든다 (`82.3% · 84.0% · 87.0% … 99.0% · 100.0%`). 간격이 불규칙한 축은
 * 읽는 사람이 없는 규칙을 찾게 만든다. 눈금 간격을 먼저 고르고 경계를 거기에
 * 맞춰 넓힌다.
 */
export function niceBand(lo: number, hi: number, target = 5) {
  const span = Math.max(hi - lo, 1e-9)
  const raw = span / target
  const mag = Math.pow(10, Math.floor(Math.log10(raw)))
  const interval = (NICE.find((s) => raw <= s * mag) ?? 10) * mag
  const round = (v: number) => Number(v.toFixed(10))
  return {
    min: round(Math.floor(lo / interval) * interval),
    max: round(Math.ceil(hi / interval) * interval),
    interval: round(interval),
  }
}

export function axisStyle(ink3: string, hairline: string) {
  return {
    axisLine: { lineStyle: { color: hairline } },
    axisTick: { show: false },
    axisLabel: { fontSize: 11, color: ink3 },
    splitLine: { lineStyle: { color: hairline, type: 'solid' as const, width: 1 } },
  }
}

function parseHex(c: string): [number, number, number] | null {
  const m = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(c.trim())
  const g = m?.[1]
  if (!g) return null
  const h = g.length === 3 ? [...g].map((x) => x + x).join('') : g
  return [0, 2, 4].map((i) => parseInt(h.slice(i, i + 2), 16)) as [number, number, number]
}

/**
 * 순서가 있는 계열(연식·정격 전력)의 색 — **한 색상의 명도 단계**.
 *
 * 범주색은 두 개뿐이고 늘리지 않는다. 연식 9개를 범주색으로 그리면 무지개가 되고
 * 순서가 사라진다 — 순서형 축은 명도로 나눈다. 0번이 가장 옅고 마지막이 가장 짙다.
 * 배경색 쪽으로 섞으므로 어두운 바탕에서도 같은 규칙이 선다.
 */
export function seqRamp(n: number): string[] {
  const base = cssVar('--c-cat-1', '#0066cc')
  const bg = cssVar('--c-canvas', '#ffffff')
  const a = parseHex(base)
  const b = parseHex(bg)
  if (!a || !b) return Array.from({ length: n }, () => base)
  return Array.from({ length: n }, (_, i) => {
    const t = n <= 1 ? 0 : 0.72 * (1 - i / (n - 1))
    const c = a.map((v, k) => Math.round(v + ((b[k] ?? v) - v) * t))
    return `#${c.map((v) => v.toString(16).padStart(2, '0')).join('')}`
  })
}

/** 램프에서 i 번째 색. 범위를 벗어나면 기준색 — 인덱스 접근이 undefined 를 낼 수 있다 */
export function rampAt(colors: readonly string[], i: number): string {
  return colors[i] ?? cssVar('--c-cat-1', '#0066cc')
}
